import {
	QuantityConfig,
	QuantityConfigCondition,
	QuantityConfigType,
} from '@/api/ComponentVariationApi';
import { RollerBoxHeight } from '@/api/ProductTypeApi';
import { ConfigurationArticle } from '@/api/ProjectApi';
import {
	CreatorSegment,
	CreatorState,
	CreatorWindow,
	CreatorWing,
	SegmentDivisionType,
} from '@/types/creator';
import {
	evaluateFormula,
	getSegmentSequences,
	getWingSequences,
	hasMosquitoNetRunner,
} from '@/utils/creatorUtils';
import { WritableDraft } from '@reduxjs/toolkit';

type QuantityConfigsByType = Record<QuantityConfigType, QuantityConfig[]>;
type SequenceEdge = SegmentDivisionType | 'WALL' | 'MOSQUITO_NET_EDGE';
type SequenceEdges = [start: SequenceEdge, end: SequenceEdge];

interface MosquitoNetRunners {
	left: number;
	right: number;
}

interface FormulaScope {
	width: number;
	windowHeight: number;
	mosquitoNetRunners: MosquitoNetRunners;
}

class ConfigurationArticlesQuantityCalculator {
	private readonly formulaVariable = {
		PW: '$pw',
		PH: '$ph',
		SSW: '$ssw',
		SSH: '$ssh',
		SSWH: '$sswh',

		RW: '$rw',
		RH: '$rh',
		RD: '$rd',

		LPW: '$lpw',
		LPSZ: '$lpsz',
		LPSM_L: '$lpsmL',
		LPSM_P: '$lpsmP',

		LLBA: '$llba',
	} as const;

	constructor() {}

	private groupQuantityConfigsByType(quantityConfigs: QuantityConfig[]) {
		const grouped: QuantityConfigsByType = {
			[QuantityConfigType.PROJECT]: [],
			[QuantityConfigType.ROLLER_BOX_SEGMENT]: [],
			[QuantityConfigType.MOSQUITO_NET_SEGMENT]: [],
		};

		for (const quantityConfig of quantityConfigs)
			grouped[quantityConfig.type].push(quantityConfig);

		return grouped;
	}

	private computeQuantity(
		configurationArticle: ConfigurationArticle,
		state: WritableDraft<CreatorState>
	) {
		const { quantityConfigs } = configurationArticle.componentVariationArticle;

		if (!quantityConfigs.length) return;

		configurationArticle.quantity = 0;

		const grouped = this.groupQuantityConfigsByType(quantityConfigs);

		for (const key in grouped) {
			const type = key as QuantityConfigType;
			const quantityConfigs = grouped[type];

			if (!quantityConfigs.length) continue;

			switch (type) {
				case QuantityConfigType.PROJECT:
					this.computeProjectQuantity(
						configurationArticle,
						quantityConfigs,
						state
					);
					break;
				case QuantityConfigType.ROLLER_BOX_SEGMENT:
					this.computeRollerBoxSegmentQuantity(
						configurationArticle,
						quantityConfigs,
						state
					);
					break;
				case QuantityConfigType.MOSQUITO_NET_SEGMENT:
					this.computeMosquitoSegmentQuantity(
						configurationArticle,
						quantityConfigs,
						state
					);
					break;
			}
		}
	}

	private computeProjectQuantity(
		configurationArticle: ConfigurationArticle,
		quantityConfigs: QuantityConfig[],
		state: WritableDraft<CreatorState>
	) {
		const { width, height, rollerBoxHeight } = state.projectDimensions;
		const quantityConfig = this.getMatchingProjectQuantityConfig(
			quantityConfigs,
			rollerBoxHeight
		);

		if (!quantityConfig) return;

		const { formulaVariable } = this;

		const variables = {
			...this.getFormulaVariables(state, {
				width,
				windowHeight: height - rollerBoxHeight,
				mosquitoNetRunners: this.countSegmentsMosquitoNetRunners(
					state.segments
				),
			}),
			[formulaVariable.LPW]: this.countDivisions(
				state,
				SegmentDivisionType.THIN
			),
			[formulaVariable.LPSZ]: this.countDivisions(
				state,
				SegmentDivisionType.WIDE
			),
		};

		configurationArticle.quantity += Math.ceil(
			evaluateFormula(quantityConfig.formula, variables)
		);
	}

	private getMatchingProjectQuantityConfig(
		quantityConfigs: QuantityConfig[],
		rollerBoxHeight: RollerBoxHeight
	) {
		for (const quantityConfig of quantityConfigs) {
			if (quantityConfig.condition === null) return quantityConfig;

			switch (quantityConfig.condition) {
				case QuantityConfigCondition.ROLLER_BOX_HEIGHT:
					if (this.isRollerBoxHeightMatching(quantityConfig, rollerBoxHeight))
						return quantityConfig;
					break;
				default:
					continue;
			}
		}

		return null;
	}

	private isRollerBoxHeightMatching(
		quantityConfig: QuantityConfig,
		rollerBoxHeight: RollerBoxHeight
	) {
		return rollerBoxHeight === Number(quantityConfig.conditionValue);
	}

	private countDivisions(
		state: WritableDraft<CreatorState>,
		divisionType: SegmentDivisionType
	) {
		return state.segmentJoints.filter(
			(joint) => joint.divisionType === divisionType
		).length;
	}

	private computeRollerBoxSegmentQuantity(
		configurationArticle: ConfigurationArticle,
		quantityConfigs: QuantityConfig[],
		state: WritableDraft<CreatorState>
	) {
		const segmentSequences = getSegmentSequences(
			state.segments,
			state.segmentJoints
		);
		const divisions = state.segmentJoints
			.map((joint) => joint.divisionType)
			.filter((divisionType) => divisionType !== SegmentDivisionType.NONE);

		for (const [idx, segmentSequence] of segmentSequences.entries()) {
			const sequenceEdges: SequenceEdges = [
				divisions[idx - 1] ?? 'WALL',
				divisions[idx] ?? 'WALL',
			];

			const quantityConfig = this.getMatchingSegmentQuantityConfig(
				quantityConfigs,
				sequenceEdges,
				state.projectDimensions.rollerBoxHeight
			);

			if (!quantityConfig) continue;

			const variables = {
				...this.getFormulaVariables(state, {
					width: segmentSequence.reduce((acc, curr) => acc + curr.width, 0),
					windowHeight: segmentSequence[0].window.height,
					mosquitoNetRunners:
						this.countSegmentsMosquitoNetRunners(segmentSequence),
				}),
				[this.formulaVariable.LLBA]: segmentSequence.reduce(
					(acc, curr) => acc + curr.nonPerforatedSlatCount,
					0
				),
			};

			configurationArticle.quantity += Math.ceil(
				evaluateFormula(quantityConfig.formula, variables)
			);
		}
	}

	private getMatchingSegmentQuantityConfig(
		quantityConfigs: QuantityConfig[],
		sequenceEdges: SequenceEdges,
		rollerBoxHeight: RollerBoxHeight
	) {
		for (const quantityConfig of quantityConfigs) {
			if (quantityConfig.condition === null) return quantityConfig;

			switch (quantityConfig.condition) {
				case QuantityConfigCondition.ROLLER_BOX_HEIGHT:
					if (this.isRollerBoxHeightMatching(quantityConfig, rollerBoxHeight))
						return quantityConfig;
					break;
				case QuantityConfigCondition.AE_TO_AE:
					if (this.isSequenceBetween(sequenceEdges, 'WALL', 'WALL'))
						return quantityConfig;
					break;
				case QuantityConfigCondition.AE_TO_AC:
					if (
						this.isSequenceBetween(
							sequenceEdges,
							'WALL',
							SegmentDivisionType.THIN
						) ||
						this.isSequenceBetween(
							sequenceEdges,
							'WALL',
							SegmentDivisionType.WIDE
						)
					)
						return quantityConfig;
					break;
				case QuantityConfigCondition.AE_TO_AC_WIDE:
					if (
						this.isSequenceBetween(
							sequenceEdges,
							'WALL',
							SegmentDivisionType.WIDE
						)
					)
						return quantityConfig;
					break;
				case QuantityConfigCondition.AC_THIN_TO_AC_THIN:
					if (
						this.isSequenceBetween(
							sequenceEdges,
							SegmentDivisionType.THIN,
							SegmentDivisionType.THIN
						)
					)
						return quantityConfig;
					break;
				case QuantityConfigCondition.AC_THIN_TO_AC_WIDE:
					if (
						this.isSequenceBetween(
							sequenceEdges,
							SegmentDivisionType.THIN,
							SegmentDivisionType.WIDE
						)
					)
						return quantityConfig;
					break;
				case QuantityConfigCondition.AC_WIDE_TO_AC_WIDE:
					if (
						this.isSequenceBetween(
							sequenceEdges,
							SegmentDivisionType.WIDE,
							SegmentDivisionType.WIDE
						)
					)
						return quantityConfig;
					break;
				default:
					continue;
			}
		}

		return null;
	}

	private isSequenceBetween(
		[startEdge, endEdge]: SequenceEdges,
		firstEdge: SequenceEdge,
		secondEdge: SequenceEdge
	) {
		return (
			(startEdge === firstEdge && endEdge === secondEdge) ||
			(startEdge === secondEdge && endEdge === firstEdge)
		);
	}

	private computeMosquitoSegmentQuantity(
		configurationArticle: ConfigurationArticle,
		quantityConfigs: QuantityConfig[],
		state: WritableDraft<CreatorState>
	) {
		if (!state.configurationConfig.project.allowMosquitoNet) return;

		const wingSequences = this.getWingSequencesWithEdges(state);

		for (const {
			wingSequence,
			sequenceEdges,
			creatorWindow,
		} of wingSequences) {
			if (!wingSequence.some((wing) => wing.withMosquitoNet)) continue;

			const quantityConfig = this.getMatchingMosquitoNetQuantityConfig(
				quantityConfigs,
				sequenceEdges
			);

			if (!quantityConfig) continue;

			const variables = this.getFormulaVariables(state, {
				width: wingSequence.reduce((acc, curr) => {
					if (!curr.withMosquitoNet) return acc;
					return acc + curr.width;
				}, 0),
				windowHeight: creatorWindow.height,
				mosquitoNetRunners: this.countMosquitoNetRunners(
					creatorWindow,
					wingSequence
				),
			});

			configurationArticle.quantity += Math.ceil(
				evaluateFormula(quantityConfig.formula, variables)
			);
		}
	}

	private getWingSequencesWithEdges(state: WritableDraft<CreatorState>) {
		return state.segments.flatMap((segment, segmentIdx) => {
			const segmentStartEdge =
				state.segmentJoints[segmentIdx - 1]?.divisionType ?? 'WALL';
			const segmentEndEdge =
				state.segmentJoints[segmentIdx]?.divisionType ?? 'WALL';

			const wingSequences = getWingSequences(
				segment.window.wings,
				segment.window.wingDivisions
			);

			return wingSequences.map((wingSequence, idx) => {
				const sequenceEdges: SequenceEdges = [
					idx === 0 ? segmentStartEdge : 'MOSQUITO_NET_EDGE',
					idx === wingSequences.length - 1
						? segmentEndEdge
						: 'MOSQUITO_NET_EDGE',
				];

				return { wingSequence, sequenceEdges, creatorWindow: segment.window };
			});
		});
	}

	private getMatchingMosquitoNetQuantityConfig(
		quantityConfigs: QuantityConfig[],
		sequenceEdges: SequenceEdges
	) {
		for (const quantityConfig of quantityConfigs) {
			if (quantityConfig.condition === null) return quantityConfig;

			switch (quantityConfig.condition) {
				case QuantityConfigCondition.AC_THIN_TO_AC_THIN:
					if (
						this.isSequenceBetween(
							sequenceEdges,
							SegmentDivisionType.THIN,
							SegmentDivisionType.THIN
						)
					)
						return quantityConfig;
					break;
				case QuantityConfigCondition.AC_THIN_TO_AC_WIDE:
					if (
						this.isSequenceBetween(
							sequenceEdges,
							SegmentDivisionType.THIN,
							SegmentDivisionType.WIDE
						)
					)
						return quantityConfig;
					break;
				case QuantityConfigCondition.AC_WIDE_TO_AC_WIDE:
					if (
						this.isSequenceBetween(
							sequenceEdges,
							SegmentDivisionType.WIDE,
							SegmentDivisionType.WIDE
						)
					)
						return quantityConfig;
					break;
				case QuantityConfigCondition.AC_TO_MOSQUITO_NET_EDGE:
					if (
						Object.values(SegmentDivisionType).some((divisionType) =>
							this.isSequenceBetween(
								sequenceEdges,
								divisionType,
								'MOSQUITO_NET_EDGE'
							)
						)
					)
						return quantityConfig;
					break;
				case QuantityConfigCondition.AE_TO_MOSQUITO_NET_EDGE:
					if (
						this.isSequenceBetween(sequenceEdges, 'WALL', 'MOSQUITO_NET_EDGE')
					)
						return quantityConfig;
					break;
				case QuantityConfigCondition.AC_THIN_TO_MOSQUITO_NET_EDGE:
					if (
						this.isSequenceBetween(
							sequenceEdges,
							SegmentDivisionType.THIN,
							'MOSQUITO_NET_EDGE'
						)
					)
						return quantityConfig;
					break;
				case QuantityConfigCondition.AC_WIDE_TO_MOSQUITO_NET_EDGE:
					if (
						this.isSequenceBetween(
							sequenceEdges,
							SegmentDivisionType.WIDE,
							'MOSQUITO_NET_EDGE'
						)
					)
						return quantityConfig;
					break;
				case QuantityConfigCondition.MOSQUITO_NET_EDGE_TO_MOSQUITO_NET_EDGE:
					if (
						this.isSequenceBetween(
							sequenceEdges,
							'MOSQUITO_NET_EDGE',
							'MOSQUITO_NET_EDGE'
						)
					)
						return quantityConfig;
					break;
				default:
					continue;
			}
		}

		return null;
	}

	private countMosquitoNetRunners(
		creatorWindow: CreatorWindow,
		countedWings: CreatorWing[] = creatorWindow.wings
	) {
		const runners: MosquitoNetRunners = { left: 0, right: 0 };
		const { wings, wingDivisions } = creatorWindow;

		wings.forEach((wing, idx) => {
			if (!wing.withMosquitoNet) return;
			if (!countedWings.some((countedWing) => countedWing.id === wing.id))
				return;

			if (
				hasMosquitoNetRunner(
					wingDivisions[idx - 1],
					wings[idx - 1]?.withMosquitoNet
				)
			)
				runners.left++;

			if (
				hasMosquitoNetRunner(
					wingDivisions[idx],
					wings[idx + 1]?.withMosquitoNet
				)
			)
				runners.right++;
		});

		return runners;
	}

	private countSegmentsMosquitoNetRunners(segments: CreatorSegment[]) {
		return segments.reduce<MosquitoNetRunners>(
			(acc, segment) => {
				const runners = this.countMosquitoNetRunners(segment.window);

				return {
					left: acc.left + runners.left,
					right: acc.right + runners.right,
				};
			},
			{ left: 0, right: 0 }
		);
	}

	private getFormulaVariables(
		state: WritableDraft<CreatorState>,
		{ width, windowHeight, mosquitoNetRunners }: FormulaScope
	) {
		const {
			width: projectWidth,
			height: projectHeight,
			rollerBoxHeight,
			rollerBoxDepth,
		} = state.projectDimensions;
		const { formulaVariable } = this;

		return {
			[formulaVariable.PW]: projectWidth,
			[formulaVariable.PH]: projectHeight,
			[formulaVariable.SSW]: width,
			[formulaVariable.SSH]: windowHeight + rollerBoxHeight,
			[formulaVariable.SSWH]: windowHeight,
			[formulaVariable.RW]: projectWidth,
			[formulaVariable.RH]: rollerBoxHeight,
			[formulaVariable.RD]: rollerBoxDepth,
			[formulaVariable.LPSM_L]: mosquitoNetRunners.left,
			[formulaVariable.LPSM_P]: mosquitoNetRunners.right,
		};
	}

	setConfigurationArticlesQuantity(state: WritableDraft<CreatorState>) {
		for (const configurationArticle of state.creatorProject
			.configurationArticles) {
			this.computeQuantity(configurationArticle, state);
		}
	}
}

export default new ConfigurationArticlesQuantityCalculator();
